import { Flex } from '@chakra-ui/layout';
import {
    Button,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useToast,
    ButtonGroup,
    IconButton,
    useEditableControls,
    Editable,
    EditablePreview,
    EditableInput,
    Input,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useState } from 'react';
import { FaCheck, FaTimes, FaEdit } from 'react-icons/fa';
import SelectedGameContext from '../context/SelectedGameContext';
import { BranchData, createBranch } from '../util/branch';

const EditableControls = () => {
    const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } =
        useEditableControls();

    return isEditing ? (
        <ButtonGroup justifyContent="center" size="sm">
            <IconButton aria-label="" icon={<FaCheck />} {...getSubmitButtonProps()} />
            <IconButton aria-label="" icon={<FaTimes />} {...getCancelButtonProps()} />
        </ButtonGroup>
    ) : (
        <Flex justifyContent="center">
            <IconButton aria-label="" size="sm" icon={<FaEdit />} {...getEditButtonProps()} />
        </Flex>
    );
};

interface BranchBrowserProps {
    showActorBuild: boolean;
    branches: BranchData[];
    refreshPage: any;
}

export const BranchBrowser: React.FC<BranchBrowserProps> = ({
    showActorBuild,
    branches: devBranches,
    refreshPage,
}) => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const { keycloak } = useKeycloak();
    const toast = useToast();
    const [newBranchName, setNewBranchName] = useState('');
    const [newBranchDesc, setNewBranchDesc] = useState('');

    return (
        <Table variant="simple" data-cy="branch-table">
            <Thead>
                <Tr>
                    <Th>Branch</Th>
                    <Th>Description</Th>
                    {showActorBuild ? <Th>Actor Build ID</Th> : <></>}
                    <Th>RMB Build ID</Th>
                </Tr>
            </Thead>
            <Tbody>
                {devBranches.map((branchData, i) => {
                    if (!branchData) return <></>;
                    return (
                        <Tr key={`branch_${i}`}>
                            <Td>{branchData.branchName}</Td>
                            <Td>
                                <Editable
                                    textAlign="center"
                                    defaultValue={branchData.description}
                                    isPreviewFocusable={false}
                                    selectAllOnFocus={true}
                                    onSubmit={async (nextValue: string) => {
                                        if (branchData.description != nextValue) {
                                            branchData.description = nextValue;
                                            try {
                                                const newBranchData = await createBranch(
                                                    currLicense.key,
                                                    branchData.description,
                                                    branchData.branchName,
                                                    keycloak.token,
                                                );
                                                toast({
                                                    title: 'Success!',
                                                    description: `Branch ${newBranchData?.branchName} updated successfully`,
                                                    status: 'success',
                                                    duration: 9000,
                                                    isClosable: true,
                                                });
                                            } catch (err) {
                                                return toast({
                                                    title: 'Error',
                                                    description:
                                                        err instanceof Error
                                                            ? err.message
                                                            : `Unexpected error ${err}`,
                                                    status: 'error',
                                                    duration: 9000,
                                                    isClosable: true,
                                                });
                                            }
                                        }
                                    }}
                                >
                                    <Flex>
                                        <EditablePreview />
                                        <Input as={EditableInput} />
                                        <EditableControls />
                                    </Flex>
                                </Editable>
                            </Td>
                            {showActorBuild ? (
                                <Td>{branchData.assignBuilds?.actor?.buildId}</Td>
                            ) : (
                                <></>
                            )}
                            <Td>{branchData.assignBuilds?.rmb?.buildId}</Td>
                        </Tr>
                    );
                })}
                <Tr>
                    <Td>
                        <Input
                            name="branchName"
                            placeholder="Branch Name"
                            marginBottom="0.5em"
                            onChange={(e) => {
                                setNewBranchName(e.target.value);
                            }}
                            value={newBranchName}
                        />
                    </Td>
                    <Td>
                        <Input
                            name="description"
                            placeholder="Description"
                            marginBottom="0.5em"
                            onChange={(e) => {
                                setNewBranchDesc(e.target.value);
                            }}
                            value={newBranchDesc}
                        />
                    </Td>
                    <Td>
                        <Button
                            type="submit"
                            marginTop="0.5em"
                            disabled={newBranchName === '' || newBranchDesc === ''}
                            onClick={async () => {
                                try {
                                    const newBranchData = await createBranch(
                                        currLicense.key,
                                        newBranchDesc,
                                        newBranchName,
                                        keycloak.token,
                                    );
                                    toast({
                                        title: 'Success!',
                                        description: `Branch ${newBranchData?.branchName} created successfully`,
                                        status: 'success',
                                        duration: 9000,
                                        isClosable: true,
                                    });
                                    setNewBranchName('');
                                    setNewBranchDesc('');
                                    refreshPage();
                                } catch (err) {
                                    return toast({
                                        title: 'Error',
                                        description:
                                            err instanceof Error
                                                ? err.message
                                                : `Unexpected error ${err}`,
                                        status: 'error',
                                        duration: 9000,
                                        isClosable: true,
                                    });
                                }
                            }}
                        >
                            Create new branch
                        </Button>
                    </Td>
                </Tr>
            </Tbody>
        </Table>
    );
};
